import { searchLogger, searchErrorLogger } from '@/core/Logger'
import { createApp, ref } from 'vue'
import AutocompletePopup from './components/AutocompletePopup/AutocompletePopup.vue'
import SearchInput from './components/SearchOnlyInput.vue'
import SerpPage from './components/SerpPage/SerpPage.vue'

import { SmartSearchConfigInterface, SmartSearchTranslationsInterface } from './core/types'
import {
  restoreDefaultSettings,
  fetchFrontSettings,
  searchIsConfigured,
  deepAssign,
  showAsStylisationList,
  overrideSettings
} from './core/utils'
import { createPinia } from 'pinia'
import DefaultConfig from '../public/config/DefaultConfig.json'
import DefaultLabels from '../public/labels/DefaultLabels.json'

import VueLazyload from 'vue-lazyload'
import ClickOutsideDirective from "@/core/ClickOutsideDirective"

const { VUE_APP_VERSION } = process.env

const SmartSearchConfig = ref<SmartSearchConfigInterface>(DefaultConfig)
const SmartSearchTranslations = ref<SmartSearchTranslationsInterface>(DefaultLabels)

searchLogger.log(`Version: ${VUE_APP_VERSION}`)

const pinia = createPinia()

// Globalne obiekty w _QONizer, by przechować flagi mountu
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer = window._QONizer || {}
// Dodajemy flagi, jeśli nie istnieją
if (window?._QONizer?._initialized === undefined) {
  window._QONizer._initialized = {
    autocomplete: false,
    searchInput: false,
    serpPage: false
  }
}

/**
 * Sprawdza, czy elementy VUE są już zmontowane
 */
function isMounted(selector: string): boolean {
  const element = document.querySelector(selector)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return element && element.__vue_app__ !== undefined
}

/**
 * Inicjalizacja AutocompletePopup
 */
async function initializeAutocomplete(selector = '#smartsearch-autocomplete') {
  // Sprawdzamy globalną flagę, czy nie jest już zainicjalizowane
  if (window?._QONizer?._initialized?.autocomplete) {
    searchLogger.log('Autocomplete is already initialized. Skipping.')
    return
  }
  
  searchLogger.log('Initialising Autocomplete')
  const autocompleteApp = createApp(AutocompletePopup)
  
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  autocompleteApp.component('searchInput', SmartSearchConfig.value.serp.customComponents.topProduct)
  autocompleteApp.directive('click-outside', ClickOutsideDirective)
  autocompleteApp.provide('SmartSearchConfig', SmartSearchConfig)
  autocompleteApp.provide('SmartSearchTranslations', SmartSearchTranslations)
  autocompleteApp.use(pinia)
  autocompleteApp.use(VueLazyload)
  autocompleteApp.mount(selector)
  
  setTimeout(() => {
    if (!isMounted(selector)) {
      searchLogger.log('Retrying to mount Autocomplete after 300ms')
      autocompleteApp.mount(selector)
    }
  }, 300)
  
  if (isMounted(selector)) {
    searchLogger.log('Autocomplete mounted successfully')
    // Ustawiamy flagę, że autocomplete jest zainicjalizowane
    window._QONizer._initialized.autocomplete = true
  }
}

/**
 * Inicjalizacja SearchInput
 */
async function initializeSearchInput(selector = '#smartsearch-searchInput') {
  // Sprawdzamy globalną flagę
  if (window._QONizer._initialized.searchInput) {
    searchLogger.log('SearchInput is already initialized. Skipping.')
    return
  }
  
  searchLogger.log('Initialising SearchInput')
  const SearchInputApp = createApp(SearchInput)
  SearchInputApp.directive('click-outside', ClickOutsideDirective)
  SearchInputApp.provide('SmartSearchConfig', SmartSearchConfig)
  SearchInputApp.provide('SmartSearchTranslations', SmartSearchTranslations)
  SearchInputApp.use(pinia)
  SearchInputApp.use(VueLazyload)
  SearchInputApp.mount(selector)
  
  setTimeout(() => {
    if (!isMounted(selector)) {
      searchLogger.log('Retrying to mount SearchInput after 300ms')
      SearchInputApp.mount(selector)
    }
  }, 300)
  
  if (isMounted(selector)) {
    searchLogger.log('SearchInput mounted successfully')
    window._QONizer._initialized.searchInput = true
  }
}

/**
 * Inicjalizacja SerpPage
 */
async function initializeSerpPage(selector = '#smartsearch-serp') {
  if (window._QONizer._initialized.serpPage) {
    searchLogger.log('SerpPage is already initialized. Skipping.')
    return
  }
  
  searchLogger.log('Initialising SerpPage')
  const serpApp = createApp(SerpPage)
  serpApp.directive('click-outside', ClickOutsideDirective)
  serpApp.provide('SmartSearchConfig', SmartSearchConfig)
  serpApp.provide('SmartSearchTranslations', SmartSearchTranslations)
  serpApp.use(pinia)
  serpApp.use(VueLazyload)
  serpApp.mount(selector)
  
  setTimeout(() => {
    if (!isMounted(selector)) {
      searchLogger.log('Retrying to mount SerpPage after 300ms')
      serpApp.mount(selector)
    }
  }, 300)
  
  if (isMounted(selector)) {
    searchLogger.log('SerpPage mounted successfully')
    window._QONizer._initialized.serpPage = true
  }
}

/**
 * Próba re-inicjalizacji (np. w DOMContentLoaded),
 * ale tylko dla tych elementów, które jeszcze nie są zainicjalizowane
 */
async function retryMountOnLoad() {
  if (!isMounted('#smartsearch-autocomplete')) {
    await initializeAutocomplete()
  }
  if (!isMounted('#smartsearch-searchInput')) {
    await initializeSearchInput()
  }
  if (!isMounted('#smartsearch-serp')) {
    await initializeSerpPage()
  }
}

// Nasłuch DOMContentLoaded -> fallback
document.addEventListener('DOMContentLoaded', retryMountOnLoad)

// Główna asynchroniczna inicjalizacja
;(async () => {
  if (!searchIsConfigured()) {
    searchErrorLogger.log('SmartSearch needs config in window.SmartSearchConfig')
    return
  }
  
  const frontSettings = await fetchFrontSettings()
  const { settings, labels } = frontSettings
  
  const mergedConfig = deepAssign()(DefaultConfig, settings)
  const mergedLabels = deepAssign()(DefaultLabels, labels)
  
  SmartSearchTranslations.value = mergedLabels as SmartSearchTranslationsInterface
  SmartSearchConfig.value = mergedConfig as SmartSearchConfigInterface
  restoreDefaultSettings()
  showAsStylisationList()
  overrideSettings()
  
  searchLogger.log('SmartSearchTranslations: ', SmartSearchTranslations.value)
  searchLogger.log('SmartSearchConfig: ', SmartSearchConfig.value)
  
  // Jednorazowa inicjalizacja wszystkich komponentów
  // (jeśli któryś jest już zainicjalizowany, to skipnie)
  await initializeAutocomplete()
  await initializeSearchInput()
  await initializeSerpPage()
})()

// Eksporty i metody dostępne globalnie, np. z window._QONizer
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeAutocomplete = (selector = '#smartsearch-autocomplete') => initializeAutocomplete(selector)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeSerpPage = (selector = '#smartsearch-serp') => initializeSerpPage(selector)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeSearchInput = (selector = '#smartsearch-searchInput') => initializeSearchInput(selector)

// Możesz też zrobić:
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.SmartSearchInstanceConfig = SmartSearchConfig.value

export {
  SmartSearchConfig,
  SmartSearchTranslations,
  initializeAutocomplete,
  initializeSearchInput,
  initializeSerpPage
}


// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore